import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, FileText, User } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useReveal } from "@/hooks/useReveal";
import { CALENDLY_URL } from "@/lib/constants";
import { getPost, getRelated, posts, type BlogBlock, type BlogCategory } from "@/lib/blog";
import { getAuthorForSlug, AUTHORS } from "@/lib/authors";
import { rulesForCategory, type LinkRule } from "@/lib/blogLinks";
import BlogRiesterNachfolgerStub from "./BlogRiesterNachfolgerStub";

const INLINE_LINK_CLASS =
  "text-[#1A213A] font-semibold no-underline hover:underline underline-offset-2";

const MAX_LINKS_PER_ARTICLE = 6;

type LinkBudget = {
  usedRuleIds: Set<string>;
  count: number;
};

/**
 * Apply blog link rules to a single text block (paragraph or list item).
 * Rules:
 *  - Each keyword (rule) at most once per article (usedRuleIds)
 *  - At most 1 link per paragraph/block
 *  - Only the first occurrence of a keyword in the block
 *  - Hard cap of MAX_LINKS_PER_ARTICLE links per article
 */
function autoLink(
  text: string,
  rules: LinkRule[],
  budget: LinkBudget,
): ReactNode[] {
  if (budget.count >= MAX_LINKS_PER_ARTICLE) return [text];

  // Find the earliest match across all not-yet-used rules.
  let best: { rule: LinkRule; index: number; match: string } | null = null;
  for (const rule of rules) {
    if (budget.usedRuleIds.has(rule.id)) continue;
    rule.pattern.lastIndex = 0;
    const m = rule.pattern.exec(text);
    rule.pattern.lastIndex = 0;
    if (m && m.index >= 0) {
      if (!best || m.index < best.index) {
        best = { rule, index: m.index, match: m[0] };
      }
    }
  }

  if (!best) return [text];

  budget.usedRuleIds.add(best.rule.id);
  budget.count += 1;

  const before = text.slice(0, best.index);
  const after = text.slice(best.index + best.match.length);
  const linkNode = best.rule.external ? (
    <a
      key="lnk"
      href={best.rule.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={INLINE_LINK_CLASS}
    >
      {best.match}
    </a>
  ) : (
    <Link key="lnk" to={best.rule.href} className={INLINE_LINK_CLASS}>
      {best.match}
    </Link>
  );

  return [before, linkNode, after];
}

function CTABox() {
  return (
    <aside className="not-prose my-10 rounded-2xl bg-gradient-hero text-white p-6 sm:p-8 md:p-10 shadow-elegant relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-radial opacity-50 pointer-events-none" />
      <div className="relative">
        <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-balance">
          Bereit für Deine persönliche Strategie?
        </h3>
        <p className="mt-3 text-white/80 leading-relaxed text-sm sm:text-base">
          In einem kostenlosen Erstgespräch analysieren wir Deine Situation – unabhängig, digital und bundesweit.
        </p>
        <div className="mt-6">
          <Button asChild variant="hero" size="lg" className="w-full sm:w-auto">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="whitespace-normal text-center">
              Kostenlos Erstgespräch vereinbaren <ArrowRight className="w-5 h-5 shrink-0" />
            </a>
          </Button>
        </div>
      </div>
    </aside>
  );
}

function renderBlock(
  block: BlogBlock,
  idx: number,
  rules: LinkRule[],
  budget: LinkBudget,
) {
  if (block.type === "h2") {
    return (
      <h2
        key={idx}
        id={block.id}
        className="font-display text-2xl md:text-3xl font-bold text-primary mt-12 mb-4 scroll-mt-28"
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === "p") {
    // Detect "Pro: … Kontra: …" prose and render as a two-column comparison
    // table. Wording is preserved verbatim, only structure changes. Rendered
    // via renderToString so the <table> appears in the prerendered HTML.
    const proKontraMatch = /^\s*Pro\s*:\s*([\s\S]+?)\s+Kontra\s*:\s*([\s\S]+?)\s*$/i.exec(block.text);
    if (proKontraMatch) {
      const proText = proKontraMatch[1].trim().replace(/\s+$/g, "");
      const kontraText = proKontraMatch[2].trim();
      return (
        <div key={idx} className="not-prose my-6 -mx-4 sm:mx-0 overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse rounded-xl overflow-hidden shadow-card border border-border">
            <thead>
              <tr className="bg-secondary/60">
                <th scope="col" className="w-1/2 text-left font-display text-base font-bold text-primary px-5 py-3 border-b border-border">
                  Pro
                </th>
                <th scope="col" className="w-1/2 text-left font-display text-base font-bold text-primary px-5 py-3 border-b border-l border-border">
                  Kontra
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="align-top bg-card">
                <td className="px-5 py-4 text-foreground/85 leading-[1.7] text-base">
                  {proText}
                </td>
                <td className="px-5 py-4 text-foreground/85 leading-[1.7] text-base border-l border-border">
                  {kontraText}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <p key={idx} className="text-foreground/85 leading-[1.8] text-lg mb-5">
        {autoLink(block.text, rules, budget)}
      </p>
    );
  }
  if (block.type === "ul") {
    return (
      <ul key={idx} className="list-disc pl-6 space-y-2 text-foreground/85 text-lg mb-5">
        {block.items.map((it, i) => (
          <li key={i}>{autoLink(it, rules, budget)}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "p_rich") {
    return (
      <p key={idx} className="text-foreground/85 leading-[1.8] text-lg mb-5">
        {block.nodes.map((node, i) => {
          if (typeof node === "string") return <span key={i}>{node}</span>;
          if (node.external) {
            return (
              <a
                key={i}
                href={node.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={INLINE_LINK_CLASS}
              >
                {node.text}
              </a>
            );
          }
          return (
            <Link key={i} to={node.href} className={INLINE_LINK_CLASS}>
              {node.text}
            </Link>
          );
        })}
      </p>
    );
  }
  if (block.type === "faq") {
    return (
      <section key={idx} className="not-prose my-12">
        {block.title && (
          <h2
            id="faq"
            className="font-display text-2xl md:text-3xl font-bold text-primary mt-12 mb-6 scroll-mt-28"
          >
            {block.title}
          </h2>
        )}
        <Accordion type="single" collapsible className="space-y-3">
          {block.items.map((f, i) => (
            <AccordionItem
              key={i}
              value={`blog-faq-${i}`}
              className="rounded-xl border border-border bg-card px-5 shadow-card data-[state=open]:shadow-elegant transition-all"
            >
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-primary hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    );
  }
  if (block.type === "table") {
    return (
      <figure key={idx} className="not-prose my-6 -mx-4 sm:mx-0 overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse rounded-xl overflow-hidden shadow-card border border-border">
          <thead>
            <tr className="bg-secondary/60">
              {block.headers.map((h, i) => (
                <th
                  key={i}
                  scope="col"
                  className={`text-left font-display text-base font-bold text-primary px-5 py-3 border-b border-border ${i > 0 ? "border-l" : ""}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, r) => (
              <tr key={r} className="align-top bg-card border-t border-border">
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={`px-5 py-4 text-foreground/85 leading-[1.7] text-base ${c > 0 ? "border-l border-border" : ""} ${c === 0 ? "font-semibold text-primary" : ""}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {block.caption && (
          <figcaption className="text-sm text-muted-foreground mt-2 px-1">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }
  if (block.type === "cta") {
    return <CTABox key={idx} />;
  }
  return null;
}

export default function BlogPost() {
  const { slug = "" } = useParams();
  // Umgezogener Altartikel — hier statt als eigene Route behandelt,
  // damit die Routenliste schlank bleibt.
  if (slug === "riester-nachfolger-altersvorsorge-2026") {
    return <BlogRiesterNachfolgerStub />;
  }
  return <BlogPostContent key={slug} slug={slug} />;
}

function BlogPostContent({ slug }: { slug: string }) {
  useReveal();
  const post = getPost(slug);

  const [activeId, setActiveId] = useState<string>("");

  const headings = post?.content.filter((b): b is Extract<BlogBlock, { type: "h2" }> => b.type === "h2") ?? [];

  useEffect(() => {
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [slug, headings.length]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelated(post.slug, 3);
  const author = AUTHORS.find((a) => a.name === "Jann") ?? getAuthorForSlug(post.slug);
  const linkRules = rulesForCategory(post.category as BlogCategory, post.slug);
  const linkBudget: LinkBudget = { usedRuleIds: new Set<string>(), count: 0 };
  // Deterministic de-DE formatter — avoids Node vs. browser ICU differences that
  // caused hydration mismatches. Parses the ISO date as UTC and formats manually,
  // so SSG output and client output are byte-identical.
  const DE_MONTHS = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
  ];
  const _d = new Date(post.date);
  const formattedDate = `${String(_d.getUTCDate()).padStart(2, "0")}. ${DE_MONTHS[_d.getUTCMonth()]} ${_d.getUTCFullYear()}`;
  // "Stand:" line — derived from the same date the BlogPosting schema reports
  // (dateModified ?? date), so the visible line and the structured data agree.
  const _standIso = post.dateModified ?? post.date;
  const _sd = new Date(_standIso);
  const standLabel = `${DE_MONTHS[_sd.getUTCMonth()]} ${_sd.getUTCFullYear()}`;

  const postUrl = `https://finable.de/blog/${post.slug}`;
  const wordCount = post.content.reduce((sum, b) => {
    if (b.type === "p" || b.type === "h2") return sum + b.text.split(/\s+/).filter(Boolean).length;
    if (b.type === "ul") return sum + b.items.reduce((s, it) => s + it.split(/\s+/).filter(Boolean).length, 0);
    if (b.type === "p_rich")
      return sum + b.nodes.reduce((s, n) => s + (typeof n === "string" ? n : n.text).split(/\s+/).filter(Boolean).length, 0);
    if (b.type === "faq")
      return sum + b.items.reduce((s, f) => s + (f.q + " " + f.a).split(/\s+/).filter(Boolean).length, 0);
    return sum;
  }, 0);

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: post.dateModified ?? post.date,
    inLanguage: "de-DE",
    wordCount,
    ...(post.coverImage
      ? { image: post.coverImage.startsWith("http") ? post.coverImage : `https://finable.de${post.coverImage}` }
      : {}),
    mainEntityOfPage: { "@type": "WebPage", "@id": postUrl },
    author: {
      "@type": "Person",
      name: "Jann Kromminga",
      url: "https://finable.de/team/jann-kromminga",
    },
    publisher: {
      "@type": "Organization",
      name: "FINABLE GmbH",
      url: "https://finable.de",
      logo: { "@type": "ImageObject", url: "https://finable.de/logo.png" },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Start", item: "https://finable.de/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://finable.de/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: postUrl },
    ],
  };

  const faqBlock = post.content.find(
    (b): b is Extract<BlogBlock, { type: "faq" }> => b.type === "faq",
  );
  const faqSchema = faqBlock && faqBlock.items.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqBlock.items.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const supersedingPost = post.supersededBy
    ? posts.find((p) => p.slug === post.supersededBy)
    : undefined;
  const canonicalUrl = supersedingPost
    ? `https://finable.de/blog/${supersedingPost.slug}`
    : postUrl;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        canonical={canonicalUrl}
        ogType="article"
        image={post.coverImage}
        publishedTime={post.date}
        modifiedTime={post.dateModified ?? post.date}
        jsonLd={[blogPostingSchema, breadcrumbSchema, faqSchema]}
      />





      <Header />
      <main>
        {/* Article header */}
        <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="container relative max-w-4xl">
            <nav aria-label="Breadcrumb" className="mb-5 text-sm text-white/70">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:text-accent transition-colors">Start</Link>
                </li>
                <li aria-hidden="true" className="text-white/40">/</li>
                <li>
                  <Link to="/blog" className="hover:text-accent transition-colors">Blog</Link>
                </li>
                <li aria-hidden="true" className="text-white/40">/</li>
                <li aria-current="page" className="text-white/90 line-clamp-1 max-w-[60vw] md:max-w-none">
                  {post.title}
                </li>
              </ol>
            </nav>
            <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-3">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 whitespace-nowrap text-white/70 hover:text-accent text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" /> Zurück zum Blog
              </Link>
              <span className="inline-flex max-w-full items-center justify-center px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] sm:tracking-[0.12em] leading-none">
                {post.category}
              </span>
            </div>

            <h1 className="mt-5 font-display text-3xl md:text-5xl lg:text-6xl font-extrabold text-balance leading-[1.1]">
              {post.title}
            </h1>
            <div className="mt-7 flex flex-wrap gap-5 text-white/70 text-sm">
              <span className="inline-flex items-center gap-2">
                <Calendar className="w-4 h-4" /> {formattedDate}
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4" /> {post.readTime}
              </span>
              <span className="inline-flex items-center gap-2">
                <User className="w-4 h-4" /> {author.name}
              </span>
            </div>
          </div>
        </section>

        {/* Cover image */}
        {post.coverImage && (
          <section className="bg-background">
            <div className="container max-w-4xl -mt-8 mb-8 relative z-10">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full rounded-2xl object-cover aspect-video shadow-elegant"
                loading="eager"
              />
            </div>
          </section>
        )}

        {/* Article body + TOC */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container">
            <div className="lg:grid lg:grid-cols-[1fr_minmax(0,720px)_1fr] lg:gap-10">
              {/* TOC sidebar (desktop) */}
              <aside className="hidden lg:block">
                <div className="sticky top-28">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4">
                    Inhalt
                  </p>
                  <ul className="space-y-2.5 text-sm border-l border-border pl-4">
                    {headings.map((h) => {
                      const active = activeId === h.id;
                      return (
                        <li key={h.id}>
                          <a
                            href={`#${h.id}`}
                            className={`block leading-snug transition-colors ${
                              active
                                ? "text-accent font-semibold"
                                : "text-muted-foreground hover:text-primary"
                            }`}
                          >
                            {h.text}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </aside>

              {/* Article */}
              <article className="max-w-[720px] mx-auto lg:mx-0">
                {supersedingPost && (
                  <aside
                    role="note"
                    className="not-prose mb-8 rounded-xl border-l-4 border-accent bg-accent/10 px-5 py-4 text-base leading-relaxed text-foreground/90"
                  >
                    <p className="font-semibold text-primary mb-1">
                      Aktualisierte Fassung verfügbar
                    </p>
                    <p>
                      Dieser Beitrag stammt aus einem früheren Jahr. Die aktuelle
                      Fassung mit den Werten für 2026 findest Du hier:{" "}
                      <Link
                        to={`/blog/${supersedingPost.slug}`}
                        className="font-semibold text-accent hover:underline"
                      >
                        {supersedingPost.title}
                      </Link>
                      .
                    </p>
                  </aside>
                )}
                <p className="not-prose mb-8 text-sm text-muted-foreground">
                  <span className="font-semibold text-primary">Stand:</span> {standLabel}
                </p>
                {post.content.map((b, i) => renderBlock(b, i, linkRules, linkBudget))}

                {/* Inline fallback cross-links — only if the body did not
                    naturally surface enough internal links via auto-linking.
                    Renders as a real prose paragraph in the article body
                    (not a related-posts card section). */}
                {linkBudget.count < 2 && related.length >= 2 && (
                  <p className="text-foreground/85 leading-[1.8] text-lg mb-5">
                    {(() => {
                      // Anchor-text variants — seeded by slug so each article
                      // uses a different phrasing pair (avoids duplicate
                      // anchor text across the site).
                      const intros = [
                        "Vertiefend passt dazu unser Beitrag",
                        "Mehr Hintergrund findest Du im Artikel",
                        "Direkt anschlussfähig ist außerdem",
                        "Ergänzend lohnt sich ein Blick auf",
                      ];
                      const connectors = [
                        " sowie unsere Analyse zu ",
                        " – und ebenso aufschlussreich ist ",
                        " sowie der Ratgeber zu ",
                        " und der praktische Guide zu ",
                      ];
                      const seed = post.slug.length;
                      const intro = intros[seed % intros.length];
                      const connector = connectors[(seed + 1) % connectors.length];
                      const a = related[0];
                      const b = related[1];
                      return (
                        <>
                          {intro} <Link to={`/blog/${a.slug}`} className={INLINE_LINK_CLASS}>{a.title}</Link>
                          {connector}
                          <Link to={`/blog/${b.slug}`} className={INLINE_LINK_CLASS}>{b.title}</Link>.
                        </>
                      );
                    })()}
                  </p>
                )}

                {/* Author block — always Jann Kromminga, matching the
                    Article JSON-LD author. Keeping this consistent avoids
                    schema/UI mismatch and matches the /team/jann-kromminga link. */}
                <div className="mt-16 pt-10 border-t border-border flex items-start gap-5">
                  <img
                    src={author.image}
                    alt={author.name}
                    width={72}
                    height={72}
                    loading="lazy"
                    className={`shrink-0 w-[72px] h-[72px] rounded-full object-cover shadow-elegant ${author.imagePosition ?? "object-center"}`}
                  />
                  <div>
                    <Link
                      to="/team/jann-kromminga"
                      className="font-display text-lg font-bold text-primary hover:text-accent transition-colors"
                    >
                      {author.name}
                    </Link>
                    <p className="text-sm text-accent font-semibold mt-0.5">
                      Gründer &amp; lizenzierter Finanzberater (§34d/34f GewO) · FINABLE
                    </p>
                    <p className="mt-2 text-muted-foreground leading-relaxed">
                      Schreibt über Steueroptimierung, Altersvorsorge und Vermögensaufbau – verständlich,
                      transparent und ohne Vertriebsdruck.
                    </p>
                  </div>
                </div>

                {/* Sitewide editorial disclaimer — rendered by the template
                    for every article. Kept out of the schema (no JSON-LD
                    mention) and out of the wordCount / auto-link pipeline so
                    it does not pollute extractable content. */}
                <aside
                  role="note"
                  aria-label="Hinweis zum Beitrag"
                  className="not-prose mt-10 pt-6 border-t border-border text-xs sm:text-sm text-muted-foreground leading-relaxed"
                >
                  Dieser Beitrag dient der allgemeinen Information und ersetzt keine
                  individuelle Beratung. Stände und Zahlen beziehen sich auf den
                  angegebenen Zeitpunkt. Wenn Du eine persönliche Beratung zu Deiner
                  Situation möchtest, buch Dir gerne über den Termin-Button auf dieser
                  Seite ein kostenloses Erstgespräch.
                </aside>

                <div className="not-prose mt-8">
                  <Button asChild variant="hero" size="lg" className="w-full sm:w-auto">
                    <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="whitespace-normal text-center">
                      Kostenlos Erstgespräch vereinbaren <ArrowRight className="w-5 h-5 shrink-0" />
                    </a>
                  </Button>
                </div>
              </article>


              <div className="hidden lg:block" aria-hidden />
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="py-16 md:py-24 bg-secondary/30">
            <div className="container">
              <div className="text-center max-w-2xl mx-auto reveal">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-primary text-balance">
                  Das könnte Dich auch interessieren
                </h2>
              </div>
              <div className="mt-12 grid md:grid-cols-3 gap-6">
                {related.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="reveal group flex flex-col bg-card border border-border rounded-2xl overflow-hidden hover:shadow-elegant hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      {post.coverImage ? (
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-gradient-hero" />
                          <div className="absolute inset-0 bg-gradient-radial opacity-60" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <FileText className="w-12 h-12 text-white/30" />
                          </div>
                        </>
                      )}
                      <span className="absolute top-4 left-4 inline-flex items-center justify-center px-3 h-7 rounded-full bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-[0.12em]">
                        {post.category}
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-display text-lg font-bold text-primary leading-snug group-hover:text-accent transition-colors">
                        {post.title}
                      </h3>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                        Weiterlesen <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
